import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDealerModal } from '../hooks/useDealerModal';

const DealerModal: React.FC = () => {
  const { t } = useTranslation();
  const {
    isOpen,
    formData,
    isSubmitting,
    isSubmitted,
    error,
    dontShowAgain,
    handleInputChange,
    handleSubmit,
    handleClose,
    handleDontShowAgainChange,
    clearError,
  } = useDealerModal();

  if (!isOpen) return null;

  return (
    <div className='fixed inset-0 z-50 flex items-start sm:items-center justify-center p-3 sm:p-4 pt-16 sm:pt-4 bg-black/50'>
      <div className='relative w-full max-w-sm sm:max-w-md lg:max-w-6xl max-h-[75vh] sm:max-h-[90vh] bg-white rounded-lg shadow-2xl overflow-hidden flex flex-col'>
        {/* Botão fechar */}
        <button
          onClick={handleClose}
          className='absolute top-4 right-4 z-10 w-8 h-8 bg-white/90 hover:bg-white rounded-full flex items-center justify-center text-gray-600 hover:text-gray-800 transition-colors shadow-sm'
        >
          <svg
            className='w-5 h-5'
            fill='none'
            stroke='currentColor'
            viewBox='0 0 24 24'
          >
            <path
              strokeLinecap='round'
              strokeLinejoin='round'
              strokeWidth={2}
              d='M6 18L18 6M6 6l12 12'
            />
          </svg>
        </button>

        {/* Layout Mobile - Coluna única */}
        <div className='lg:hidden flex flex-col flex-1 min-h-0'>
          {/* Imagem no topo - Fixa */}
          <div className='bg-gray-100 h-[150px] sm:h-[250px] flex-shrink-0'>
            <img
              src='/avoprime-box.webp'
              alt='Avoprime Box'
              className='w-full h-full object-cover'
            />
          </div>
          
          {/* Header do formulário - Fixo */}
          <div className='flex-shrink-0 p-3 sm:p-6 lg:p-8 pb-0'>
            <div className='text-center mb-4 sm:mb-6'>
              <h3 className='text-xl sm:text-2xl font-bold text-gray-800 mb-2'>
                {t('dealerModal.title')}
              </h3>
              <p className='text-sm sm:text-base text-gray-600 mb-3'>
                {t('dealerModal.subtitle')}
              </p>
            </div>
          </div>
          
          {/* Formulário - Scrollável apenas os campos */}
          <div className='flex-1 flex flex-col min-h-0'>
            {isSubmitted ? (
              // Estado de sucesso
              <div className='flex-1 flex items-center justify-center px-3 sm:px-6 lg:px-8'>
                <div className='text-center py-4 sm:py-6 lg:py-8'>
                  <div className='w-16 h-16 sm:w-20 sm:h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6'>
                    <svg
                      className='w-10 h-10 text-green-600'
                      fill='none'
                      stroke='currentColor'
                      viewBox='0 0 24 24'
                    >
                      <path
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        strokeWidth={2}
                        d='M5 13l4 4L19 7'
                      />
                    </svg>
                  </div>
                  <h3 className='text-lg sm:text-xl lg:text-2xl font-bold text-gray-800 mb-2 sm:mb-3'>
                    {t('dealerModal.form.success')}
                  </h3>
                  <p className='text-gray-600 mb-6'>
                    {t('dealerModal.form.successMessage')}
                  </p>
                  <button
                    onClick={handleClose}
                    className='bg-green-600 hover:bg-green-700 text-white px-8 py-3 rounded-md font-medium transition-colors'
                  >
                    {t('common.close')}
                  </button>
                </div>
              </div>
            ) : (
              // Formulário de inscrição
              <form onSubmit={handleSubmit} className='flex-1 flex flex-col min-h-0'>
                {/* Campos do formulário - Scrolláveis */}
                <div className='flex-1 overflow-y-auto px-3 sm:px-6 lg:px-8 space-y-3 sm:space-y-5'>
                  {/* Nome */}
                  <div>
                    <label
                      htmlFor='name'
                      className='block text-sm font-medium text-gray-700 mb-2'
                    >
                      {t('dealerModal.form.name')}{' '}
                      <span className='text-red-500'>*</span>
                    </label>
                    <input
                      type='text'
                      id='name'
                      name='name'
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder={t('dealerModal.form.placeholder.name')}
                      required
                      className='w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors text-sm sm:text-base'
                    />
                  </div>

                  {/* Email */}
                  <div>
                    <label
                      htmlFor='email'
                      className='block text-sm font-medium text-gray-700 mb-2'
                    >
                      {t('dealerModal.form.email')}{' '}
                      <span className='text-red-500'>*</span>
                    </label>
                    <input
                      type='email'
                      id='email'
                      name='email'
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder={t('dealerModal.form.placeholder.email')}
                      required
                      className='w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors text-sm sm:text-base'
                    />
                  </div>

                  {/* Telefone */}
                  <div>
                    <label
                      htmlFor='phone'
                      className='block text-sm font-medium text-gray-700 mb-2'
                    >
                      {t('dealerModal.form.phone')}
                    </label>
                    <input
                      type='tel'
                      id='phone'
                      name='phone'
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder={t('dealerModal.form.placeholder.phone')}
                      className='w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors text-sm sm:text-base'
                    />
                  </div>

                  {/* Empresa */}
                  <div>
                    <label
                      htmlFor='company'
                      className='block text-sm font-medium text-gray-700 mb-2'
                    >
                      {t('dealerModal.form.company')}
                    </label>
                    <input
                      type='text'
                      id='company'
                      name='company'
                      value={formData.company}
                      onChange={handleInputChange}
                      placeholder={t('dealerModal.form.placeholder.company')}
                      className='w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors text-sm sm:text-base'
                    />
                  </div>

                  {/* Mensagem de erro */}
                  {error && (
                    <div className='bg-red-50 border border-red-200 rounded-md p-4'>
                      <div className='flex items-center'>
                        <svg className='w-5 h-5 text-red-400 mr-3 flex-shrink-0' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                          <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z' />
                        </svg>
                        <span className='text-red-800 text-sm'>{error}</span>
                        <button
                          type='button'
                          onClick={clearError}
                          className='ml-auto text-red-400 hover:text-red-600 transition-colors'
                        >
                          <svg className='w-4 h-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                            <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M6 18L18 6M6 6l12 12' />
                          </svg>
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Checkbox e Botão - Fixos no bottom */}
                <div className='flex-shrink-0 px-3 sm:px-6 lg:px-8 pb-3 sm:pb-6 lg:pb-8 space-y-3'>
                  {/* Checkbox */}
                  <div className='flex items-center pt-2'>
                    <input
                      type='checkbox'
                      id='dontShowAgain'
                      checked={dontShowAgain}
                      onChange={handleDontShowAgainChange}
                      className='w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500'
                    />
                    <label htmlFor='dontShowAgain' className='ml-3 text-sm text-gray-600'>
                      {t('dealerModal.dontShowAgain')}
                    </label>
                  </div>

                  {/* Botão de envio */}
                  <button
                    type='submit'
                    disabled={isSubmitting}
                    className='w-full bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white py-2 sm:py-3 px-6 rounded-md font-medium transition-colors disabled:cursor-not-allowed text-sm sm:text-base'
                  >
                    {isSubmitting ? (
                      <span className='flex items-center justify-center'>
                        <svg className='animate-spin -ml-1 mr-3 h-5 w-5 text-white' xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24'>
                          <circle className='opacity-25' cx='12' cy='12' r='10' stroke='currentColor' strokeWidth='4'></circle>
                          <path className='opacity-75' fill='currentColor' d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'></path>
                        </svg>
                        {t('dealerModal.form.sending')}
                      </span>
                    ) : (
                      t('dealerModal.cta')
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>

        {/* Layout Desktop - Duas colunas */}
        <div className='hidden lg:grid lg:grid-cols-5 min-h-0'>
          {/* Seção da imagem - Esquerda */}
          <div className='lg:col-span-2 bg-gray-100 h-full'>
            <img
              src='/avoprime-box.webp'
              alt='Avoprime Box'
              className='w-full h-full object-cover'
            />
          </div>

          {/* Seção do formulário - Direita */}
          <div className='lg:col-span-3 p-6 lg:p-8 flex flex-col overflow-y-auto min-h-0'>

            {isSubmitted ? (
              // Estado de sucesso
              <div className='text-center py-4 sm:py-6 lg:py-8'>
                <div className='w-16 h-16 sm:w-20 sm:h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6'>
                  <svg
                    className='w-10 h-10 text-green-600'
                    fill='none'
                    stroke='currentColor'
                    viewBox='0 0 24 24'
                  >
                    <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth={2}
                      d='M5 13l4 4L19 7'
                    />
                  </svg>
                </div>
                <h3 className='text-lg sm:text-xl lg:text-2xl font-bold text-gray-800 mb-2 sm:mb-3'>
                  {t('dealerModal.form.success')}
                </h3>
                <p className='text-gray-600 mb-6'>
                  {t('dealerModal.form.successMessage')}
                </p>
                <button
                  onClick={handleClose}
                  className='bg-green-600 hover:bg-green-700 text-white px-8 py-3 rounded-md font-medium transition-colors'
                >
                  {t('common.close')}
                </button>
              </div>
            ) : (
              // Formulário de inscrição
              <div>
                <div className='text-center mb-6 sm:mb-8'>
                  <h3 className='text-xl sm:text-2xl font-bold text-gray-800 mb-2'>
                    {t('dealerModal.title')}
                  </h3>
                  <p className='text-sm sm:text-base text-gray-600 mb-3'>
                    {t('dealerModal.subtitle')}
                  </p>
                </div>

                <form
                  onSubmit={handleSubmit}
                  className='space-y-3 sm:space-y-5 flex-1 min-h-0'
                >
                  {/* Nome */}
                  <div>
                    <label
                      htmlFor='name'
                      className='block text-sm font-medium text-gray-700 mb-2'
                    >
                      {t('dealerModal.form.name')}{' '}
                      <span className='text-red-500'>*</span>
                    </label>
                    <input
                      type='text'
                      id='name'
                      name='name'
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder={t('dealerModal.form.placeholder.name')}
                      required
                      className='w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors text-sm sm:text-base'
                    />
                  </div>

                  {/* Email */}
                  <div>
                    <label
                      htmlFor='email'
                      className='block text-sm font-medium text-gray-700 mb-2'
                    >
                      {t('dealerModal.form.email')}{' '}
                      <span className='text-red-500'>*</span>
                    </label>
                    <input
                      type='email'
                      id='email'
                      name='email'
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder={t('dealerModal.form.placeholder.email')}
                      required
                      className='w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors text-sm sm:text-base'
                    />
                  </div>

                  {/* Telefone */}
                  <div>
                    <label
                      htmlFor='phone'
                      className='block text-sm font-medium text-gray-700 mb-2'
                    >
                      {t('dealerModal.form.phone')}
                    </label>
                    <input
                      type='tel'
                      id='phone'
                      name='phone'
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder={t('dealerModal.form.placeholder.phone')}
                      className='w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors text-sm sm:text-base'
                    />
                  </div>

                  {/* Empresa */}
                  <div>
                    <label
                      htmlFor='company'
                      className='block text-sm font-medium text-gray-700 mb-2'
                    >
                      {t('dealerModal.form.company')}
                    </label>
                    <input
                      type='text'
                      id='company'
                      name='company'
                      value={formData.company}
                      onChange={handleInputChange}
                      placeholder={t('dealerModal.form.placeholder.company')}
                      className='w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors text-sm sm:text-base'
                    />
                  </div>

                  {/* Checkbox */}
                  <div className='flex items-center pt-2'>
                    <input
                      type='checkbox'
                      id='dontShowAgain'
                      checked={dontShowAgain}
                      onChange={handleDontShowAgainChange}
                      className='w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500'
                    />
                    <label
                      htmlFor='dontShowAgain'
                      className='ml-3 text-sm text-gray-600'
                    >
                      {t('dealerModal.dontShowAgain')}
                    </label>
                  </div>

                  {/* Mensagem de erro */}
                  {error && (
                    <div className='bg-red-50 border border-red-200 rounded-md p-4'>
                      <div className='flex items-center'>
                        <svg
                          className='w-5 h-5 text-red-400 mr-3 flex-shrink-0'
                          fill='none'
                          stroke='currentColor'
                          viewBox='0 0 24 24'
                        >
                          <path
                            strokeLinecap='round'
                            strokeLinejoin='round'
                            strokeWidth={2}
                            d='M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z'
                          />
                        </svg>
                        <span className='text-red-800 text-sm'>{error}</span>
                        <button
                          type='button'
                          onClick={clearError}
                          className='ml-auto text-red-400 hover:text-red-600 transition-colors'
                        >
                          <svg
                            className='w-4 h-4'
                            fill='none'
                            stroke='currentColor'
                            viewBox='0 0 24 24'
                          >
                            <path
                              strokeLinecap='round'
                              strokeLinejoin='round'
                              strokeWidth={2}
                              d='M6 18L18 6M6 6l12 12'
                            />
                          </svg>
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Botão de envio */}
                  <div className='pt-4 mt-auto flex-shrink-0'>
                    <button
                      type='submit'
                      disabled={isSubmitting}
                      className='w-full bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white py-2 sm:py-3 px-6 rounded-md font-medium transition-colors disabled:cursor-not-allowed text-sm sm:text-base'
                    >
                      {isSubmitting ? (
                        <span className='flex items-center justify-center'>
                          <svg
                            className='animate-spin -ml-1 mr-3 h-5 w-5 text-white'
                            xmlns='http://www.w3.org/2000/svg'
                            fill='none'
                            viewBox='0 0 24 24'
                          >
                            <circle
                              className='opacity-25'
                              cx='12'
                              cy='12'
                              r='10'
                              stroke='currentColor'
                              strokeWidth='4'
                            ></circle>
                            <path
                              className='opacity-75'
                              fill='currentColor'
                              d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
                            ></path>
                          </svg>
                          {t('dealerModal.form.sending')}
                        </span>
                      ) : (
                        t('dealerModal.cta')
                      )}
                    </button>
                  </div>
                </form>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DealerModal;
